import { useContactsCRUDActions } from '@mm/state/actions/ContactsCRUDActions';
import { useLiveChatCRUDActions } from '@mm/state/actions/LiveChatCRUDActions';
import { SelectedContact } from '@mm/state/interfaces/contacts';
import ConnectedPageModel from '@mm/state/model/ConnectedPageModel';
import { LivechatMessageModel } from '@mm/state/model/LivechatMessageModel';
import { Actor, assign, createMachine, DoneInvokeEvent } from 'xstate';
import { pure, sendParent } from 'xstate/lib/actions';
import { parseMessageStructureAndCampaign } from './components/Messages/parseMessage';
import { remapMessages } from './shared';

export const EmptySelectedContact: SelectedContact = {
  id: 0,
  firstName: '',
  lastName: '',
  fullName: '',
  lastActive: '',
  sessions: [],
  variables: [],
  profilePic: '',
  notes: [],
  tags: [],
  recipientId: '',
  remoteId: '',
  email: '',
  gender: '',
  locale: '',
  timezone: '',
  createdAt: '',
  ctmAdId: '',
  ctmAdName: '',
  sessionsCount: 0,
  conversation: null,
  unsubscribed: false,
};

export type ContactContext = {
  model: SelectedContact;
  connectedPage?: ConnectedPageModel;
  activeChatbotId: number;
  archivedMessages: LivechatMessageModel[];
  needsToScrollRef: boolean;
  selectedSessionId: number | undefined;
  nextSessionId: number | undefined;
  previousSessionId: number | undefined;
};

export type ContactGetConversationSessionMessages = {
  type: 'LC_CONTACT_GET_CONVERSATION_SESSION_MESSAGES';
  payload: { sessionId: number; addingOlder: boolean };
};

export type ContactEvents =
  | { type: 'LC_CONTACT_REFRESH'; payload: string }
  | { type: 'LC_CONTACT_UPDATE'; payload: Partial<ContactContext> }
  | {
      type: 'LC_CONTACT_LAST_ACTIVE_UPDATE';
      payload: ReturnType<typeof parseMessageStructureAndCampaign>;
    }
  | ContactGetConversationSessionMessages;

export type ContactActor = Actor<ContactContext, ContactEvents>;

export const createContactMachine = (
  context: ContactContext,
  contactsCRUDActions: ReturnType<typeof useContactsCRUDActions>,
  liveChatCRUDActions: ReturnType<typeof useLiveChatCRUDActions>,
) =>
  createMachine<ContactContext, ContactEvents>(
    {
      context,
      predictableActionArguments: true,
      type: 'parallel',
      states: {
        contact: {
          initial: 'idle',
          states: {
            init: {
              always: {
                target: 'refreshing',
                cond: ctx => !!ctx.model.remoteId,
              },
            },
            // Note: current logic makes each contact machine independent and issuing its
            // own rest calls. It seems to be working now, especially for the primary
            // scenarios.
            // However we might want to optimize it by doing batch requests for contacts.
            // A relatively easy way to do it would be to refactoro the code so that
            // the contact machine send event for getting the data to liveChatMachine.
            // There we can keep the queue of ids of contacts to get and do batched requests
            // e.g. every 100ms if there are any, dispatching the results back to corresponding
            // actors/machine where data would be processed next.
            // The contact machine would just need to replace the invoike with a couple fo states.
            refreshing: {
              invoke: {
                src: 'retrieveContactByRemoteId',
                onDone: {
                  target: 'idle',
                  actions: pure(
                    (ctx, evt: DoneInvokeEvent<SelectedContact>) => {
                      // Note the guard is for the cases when the invokation above result is not
                      // making a REST call due to lack of needed params

                      if (evt.data) {
                        const contact = {
                          ...evt.data,
                          remoteId: evt.data.remoteId || ctx.model.remoteId,
                          sessions: evt.data.sessions.sort((a, b) =>
                            a.createdAt.localeCompare(b.createdAt),
                          ),
                        };
                        return [
                          assign({
                            model: {
                              ...ctx.model,
                              ...contact,
                            },
                          }),
                          sendParent({
                            type: 'LC_CONVERSATION_UPDATE',
                            payload: {
                              remoteUserId: contact.remoteId,
                              firstName: contact.firstName,
                              lastName: contact.lastName,
                            },
                          }),
                        ];
                      }
                    },
                  ),
                },
                // No refetching or explicit error handling logic existed in the original code.
                // Keeping a is for now to save time
                onError: {
                  target: 'idle',
                },
              },
            },
            idle: {},
          },
          on: {
            LC_CONTACT_REFRESH: {
              actions: [
                pure((ctx, evt) => {
                  if (evt.payload) {
                    return assign({
                      model: { ...ctx.model, remoteId: evt.payload },
                    });
                  }
                }),
              ],
              target: '.refreshing',
              internal: false,
              cond: (ctx, evt) => !!evt.payload,
            },
            LC_CONTACT_UPDATE: {
              actions: assign((ctx, evt) => {
                return { ...ctx, ...evt.payload };
              }),
            },
          },
        },
        archivedMessages: {
          initial: 'idle',
          states: {
            idle: {},
            fetching: {
              invoke: {
                src: 'getConversationSession',
                onDone: {
                  target: 'idle',
                  actions: pure(
                    (
                      ctx,
                      evt: DoneInvokeEvent<
                        | (ContactGetConversationSessionMessages['payload'] & {
                            remappedMessages: ReturnType<
                              typeof parseMessageStructureAndCampaign
                            >[];
                          })
                        | undefined
                      >,
                    ) => {
                      if (!evt.data || !evt.data.remappedMessages.length) {
                        return;
                      }
                      const { remappedMessages, sessionId, addingOlder } =
                        evt.data;

                      const sessionIdx = ctx.model.sessions.findIndex(
                        s => s.id === sessionId,
                      );

                      if (addingOlder) {
                        return [
                          assign({
                            ...ctx,
                            needsToScrollRef: true,
                            archivedMessages: [
                              ...ctx.archivedMessages,
                              ...remappedMessages,
                            ],
                            previousSessionId:
                              sessionIdx && sessionIdx > 0
                                ? ctx.model.sessions[sessionIdx - 1].id
                                : undefined,
                          }),
                        ];
                      }

                      return assign({
                        ...ctx,
                        archivedMessages: [
                          ...remappedMessages,
                          ...ctx.archivedMessages,
                        ],
                        nextSessionId:
                          sessionIdx &&
                          sessionIdx < ctx.model.sessions.length - 1
                            ? ctx.model.sessions[sessionIdx + 1].id
                            : undefined,
                      });
                    },
                  ),
                },
                onError: {
                  target: 'idle',
                },
              },
            },
          },
          on: {
            LC_CONTACT_GET_CONVERSATION_SESSION_MESSAGES: {
              target: '.fetching',
              internal: false,
            },
          },
        },
      },
    },
    {
      services: {
        retrieveContactByRemoteId: async ctx => {
          if (
            ctx.model.remoteId &&
            ctx.connectedPage?.id &&
            ctx.activeChatbotId !== -1
          ) {
            return contactsCRUDActions.retrieveContactByRemoteId(
              ctx.model.remoteId,
              ctx.connectedPage.id,
            );
          }
        },
        getConversationSession: async (
          ctx,
          evt: ContactGetConversationSessionMessages,
        ) => {
          const res = await liveChatCRUDActions.getConversationSession({
            remoteUserId: ctx.model.remoteId,
            selectedSessionId: evt.payload.sessionId,
          });

          if (!res) return;

          const responseJson = await res.json();

          const remappedData = remapMessages(responseJson);

          if (remappedData && remappedData.data) {
            const remappedMessages = remappedData.data.map(m =>
              parseMessageStructureAndCampaign(m),
            );

            return { remappedMessages, ...evt.payload };
          }
        },
      },
    },
  );
